import { Link } from 'react-router-dom';
import { Shield, Wrench, MapPin, Check, ArrowRight, Leaf, Eye, Package, Thermometer, HeadphonesIcon, Truck, Sparkles, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import SEO from '../components/SEO';
import FullWidthHero from '../components/FullWidthHero';
import { realPhotos, realPhotosAlt } from '../utils/realPhotos';

const AboutPage = () => {
  const expertiseAreas = [
    {
      icon: Package,
      title: "Vente de spas sélectionnés pour leur fiabilité",
      link: "/spas",
      linkText: "Découvrir nos spas",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: Wrench,
      title: "Pièces détachées et accessoires pour spa",
      link: "/pieces-accessoires",
      linkText: "Voir les pièces",
      color: "from-slate-600 to-slate-700"
    },
    {
      icon: Shield,
      title: "Produits d'entretien et consommables",
      link: "/entretien-spa",
      linkText: "Produits d'entretien",
      color: "from-teal-500 to-teal-600"
    },
    {
      icon: Thermometer,
      title: "Pompes à chaleur pour spa (étude personnalisée)",
      link: "/pompes-a-chaleur-spa",
      linkText: "En savoir plus",
      color: "from-emerald-500 to-emerald-600"
    },
    {
      icon: HeadphonesIcon,
      title: "Accompagnement, entretien et interventions techniques locales",
      link: "/contact",
      linkText: "Nous contacter",
      color: "from-orange-500 to-orange-600"
    }
  ];

  const trustPoints = [
    {
      icon: Wrench,
      text: "Expertise terrain du spa",
      color: "bg-blue-500"
    },
    {
      icon: Shield,
      text: "Garanties constructeur jusqu'à 10 ans",
      color: "bg-indigo-500"
    },
    {
      icon: Eye,
      text: "Conseils basés sur des situations réelles",
      color: "bg-teal-500"
    },
    {
      icon: Check,
      text: "Produits sélectionnés, pas empilés",
      color: "bg-emerald-500"
    },
    {
      icon: Leaf,
      text: "Approche durable et responsable",
      color: "bg-green-500"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="about-page">
      <SEO 
        title="Spécialiste du spa | Vente, pièces et entretien | Atlantique Spa Boutique"
        description="Spécialiste du spa : vente de spas, pièces détachées, accessoires, entretien et solutions techniques. Livraison France, accompagnement local."
        canonical="/a-propos"
      />
      
      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Accueil</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>À propos</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      {/* Hero / Introduction with real photo */}
      <FullWidthHero
        variant="page"
        image={realPhotos.expertise}
        imageAlt={realPhotosAlt.expertise}
        badge="Spécialiste du spa depuis des années"
        badgeIcon={Sparkles}
        title="Votre projet spa accompagné par un"
        titleHighlight="véritable spécialiste"
        subtitle="Chez Atlantique Spa Boutique, le spa n'est pas qu'un produit : c'est un projet à part entière. Vente de spas, pièces détachées, accessoires, entretien et solutions techniques."
        primaryCta={{ text: "Nous contacter", href: "/contact", icon: ArrowRight }}
        overlayPosition="left"
      />

      {/* Notre différence - highlighted box */}
      <section className="py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8f] rounded-2xl p-8 md:p-10 shadow-2xl shadow-[#1E3A5F]/20 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-400/10 rounded-full blur-3xl" />
            
            <div className="relative">
              <h2 className="text-xl md:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <span className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </span>
                Notre différence
              </h2>
              <p className="text-lg text-slate-200 leading-relaxed max-w-3xl">
                Une connaissance approfondie du spa, acquise sur le terrain, au contact des installations, des usages et des contraintes réelles.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spécialiste vs Marketplace */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Spécialiste du spa, pas marketplace
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Ce qui nous distingue des grandes plateformes généralistes
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Ce que nous sommes */}
            <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
              <h3 className="font-bold text-green-800 mb-4 flex items-center gap-2 text-lg">
                <Check className="w-6 h-6 text-green-600" />
                Ce que nous sommes
              </h3>
              <ul className="space-y-3">
                {[
                  "Spécialiste dédié exclusivement au spa",
                  "Expertise terrain : installation, SAV, maintenance",
                  "Sélection rigoureuse de produits testés",
                  "Conseils personnalisés basés sur l'expérience",
                  "Accompagnement avant et après l'achat",
                  "Pièces neuves et d'origine constructeur"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-700">
                    <Check className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Ce que nous ne sommes pas */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
              <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2 text-lg">
                <X className="w-6 h-6 text-slate-400" />
                Ce que nous ne sommes pas
              </h3>
              <ul className="space-y-3">
                {[
                  "Une marketplace multi-vendeurs",
                  "Un revendeur de produits sans expertise",
                  "Un site de dropshipping sans stock",
                  "Un intermédiaire sans connaissance technique",
                  "Un catalogue sans accompagnement",
                  "Un comparateur de prix sans conseil"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-500">
                    <X className="w-4 h-4 text-slate-400 mt-1 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Notre approche */}
      <section className="py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
              Une approche pragmatique, orientée usage et durabilité
            </h2>
            
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {[
                "Pas de promesses irréalistes",
                "Pas de discours marketing vide", 
                "Des solutions cohérentes avec l'usage réel"
              ].map((item, i) => (
                <span key={i} className="inline-flex items-center gap-2 bg-white text-slate-700 px-4 py-2.5 rounded-full text-sm font-medium shadow-md border border-slate-100">
                  <span className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </span>
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-8 md:p-10 shadow-xl shadow-slate-200/50 max-w-3xl mx-auto relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#1E3A5F] via-orange-500 to-[#1E3A5F]" />
            
            <p className="text-slate-600 text-lg leading-relaxed mb-5">
              Chaque spa, chaque installation et chaque client sont différents.
            </p>
            <p className="text-slate-600 text-lg leading-relaxed mb-5">
              Notre rôle est de proposer des solutions adaptées, qu'il s'agisse d'un achat de spa, du remplacement d'une pièce, de l'optimisation de la consommation ou de l'entretien dans le temps.
            </p>
            <p className="text-slate-800 text-lg leading-relaxed font-semibold bg-slate-50 rounded-xl p-4 border-l-4 border-[#1E3A5F]">
              Nous privilégions des équipements fiables, réparables et compatibles, afin de garantir une expérience durable et sereine.
            </p>
          </div>
        </div>
      </section>

      {/* Nos domaines d'expertise */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-slate-50 to-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
              Nos domaines d'expertise
            </h2>
            <p className="text-slate-600">Tout pour votre spa, en un seul endroit</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {expertiseAreas.map((area, i) => (
              <Card key={i} className="group relative overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                {/* Gradient top bar */}
                <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${area.color}`} />
                
                <CardContent className="p-6 pt-8">
                  <div className={`w-14 h-14 bg-gradient-to-br ${area.color} rounded-xl flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <area.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-4 text-lg leading-snug">{area.title}</h3>
                  <Link 
                    to={area.link} 
                    className="inline-flex items-center text-sm text-[#1E3A5F] hover:text-orange-500 font-semibold transition-colors group-hover:gap-2"
                  >
                    {area.linkText}
                    <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Photo terrain - Crédibilité */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                Une expertise acquise sur le terrain
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                Chaque installation, chaque intervention et chaque conseil s'appuie sur une connaissance concrète du spa, 
                de ses composants et de son fonctionnement au quotidien.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed">
                Nous privilégions des solutions durables et adaptées, parce que nous savons ce qui fonctionne 
                et ce qui permet de profiter sereinement de son spa dans le temps.
              </p>
            </div>
            <div className="order-1 lg:order-2">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="/images/terrain/spa-terrasse-bois.jpg"
                  alt="Spa installé sur terrasse bois dans un jardin"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Présence nationale / locale */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">
                Une présence nationale, un accompagnement local
              </h2>
              
              <div className="space-y-5">
                {/* Livraison nationale */}
                <div className="flex items-start gap-4 bg-gradient-to-r from-blue-50 to-slate-50 border border-blue-100 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/30">
                    <Truck className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 mb-1 text-lg">Livraison France entière</p>
                    <p className="text-slate-600">Nous livrons nos spas, pièces détachées et accessoires partout en France.</p>
                  </div>
                </div>

                {/* Interventions locales */}
                <div className="flex items-start gap-4 bg-gradient-to-r from-orange-50 to-slate-50 border border-orange-100 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-orange-500/30">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 mb-1 text-lg">Interventions locales</p>
                    <p className="text-slate-600">Pour les projets nécessitant une intervention, un accompagnement ou une installation, nous intervenons principalement en Loire-Atlantique (44), Vendée (85), Morbihan (56) et Maine-et-Loire (49).</p>
                  </div>
                </div>

                <p className="text-slate-700 pl-2 text-lg">
                  Cette double approche permet de concilier <strong className="text-[#1E3A5F]">réactivité locale</strong> et <strong className="text-[#1E3A5F]">accessibilité nationale</strong>.
                </p>
              </div>
            </div>

            {/* Zone intervention card */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A5F] to-[#0f2840] rounded-3xl transform rotate-2 scale-[1.02] opacity-20" />
              <div className="relative bg-gradient-to-br from-[#1E3A5F] via-[#2d4a6f] to-[#0f2840] rounded-2xl p-8 text-white shadow-2xl shadow-[#1E3A5F]/30 overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/20 rounded-full blur-2xl" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-400/20 rounded-full blur-2xl" />
                
                <h3 className="font-bold text-xl mb-6 flex items-center gap-3">
                  <MapPin className="w-6 h-6 text-orange-400" />
                  Zone d'intervention locale
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { code: "44", name: "Loire-Atlantique" },
                    { code: "85", name: "Vendée" },
                    { code: "56", name: "Morbihan" },
                    { code: "49", name: "Maine-et-Loire" }
                  ].map((dept) => (
                    <div key={dept.code} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center gap-3 border border-white/10 hover:bg-white/20 transition-colors">
                      <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg">
                        <span className="font-bold text-sm text-white">{dept.code}</span>
                      </div>
                      <span className="text-sm font-medium">{dept.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi nous faire confiance */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-slate-100 via-slate-50 to-blue-50 relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-orange-200/30 rounded-full blur-3xl" />
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
              Pourquoi nous faire confiance ?
            </h2>
            <p className="text-slate-600">Des engagements concrets, pas des promesses</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {trustPoints.map((point, i) => (
              <div 
                key={i} 
                className="bg-white rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-slate-100 group"
              >
                <div className={`w-14 h-14 ${point.color} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <point.icon className="w-6 h-6 text-white" />
                </div>
                <p className="text-slate-700 font-semibold text-sm leading-snug">{point.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#1E3A5F] via-[#2d5a8f] to-[#1E3A5F]" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Vous avez un projet spa ou une question ?
          </h2>
          <p className="text-slate-300 mb-8 text-lg">
            Contactez-nous pour échanger sur votre besoin
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 transition-all">
                Nous contacter
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/faq">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 gap-2 px-8 backdrop-blur-sm">
                Consulter la FAQ
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
